import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'

/**
 * Flat layout for the settings dialog panes (board h39z): no cards inside the
 * dialog — a pane is a column of sections separated by a hairline, headings
 * are small, rows put the label left and a compact control right. One spacing
 * scale for every pane: sections `py-6`, inside a section `gap-4`.
 */

/** A pane's content: left-aligned, uses the pane width up to ~720px. */
export function SettingsPage({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      data-slot="settings-page"
      className={cn(
        'flex max-w-[720px] flex-col divide-y divide-border px-4 py-2 desktop:px-8 desktop:py-2',
        className
      )}
    >
      {children}
    </div>
  )
}

export function SettingsSection({
  title,
  description,
  tone = 'default',
  children,
  className,
}: {
  title?: ReactNode
  description?: ReactNode
  /** `danger` = the final "danger zone" section of a pane. */
  tone?: 'default' | 'danger'
  children?: ReactNode
  className?: string
}) {
  return (
    <section data-slot="settings-section" className={cn('flex flex-col gap-4 py-6', className)}>
      {(title || description) && (
        <header className="space-y-1">
          {title && (
            <h3
              className={cn(
                'text-sm font-medium text-foreground',
                tone === 'danger' && 'text-destructive'
              )}
            >
              {title}
            </h3>
          )}
          {description && <p className="text-sm text-muted-foreground">{description}</p>}
        </header>
      )}
      {children}
    </section>
  )
}

/** Label (+ optional hint) on the left, a compact control on the right; wraps on narrow panes. */
export function SettingsRow({
  label,
  description,
  htmlFor,
  children,
  className,
}: {
  label: ReactNode
  description?: ReactNode
  /** Id of the control, so the label text focuses it. */
  htmlFor?: string
  children: ReactNode
  className?: string
}) {
  return (
    <div
      data-slot="settings-row"
      className={cn('flex flex-wrap items-center justify-between gap-x-6 gap-y-2', className)}
    >
      <div className="min-w-0 flex-1 basis-56 space-y-0.5">
        {htmlFor ? (
          <label htmlFor={htmlFor} className="text-sm font-medium text-foreground">
            {label}
          </label>
        ) : (
          <p className="text-sm font-medium text-foreground">{label}</p>
        )}
        {description && <div className="text-xs text-muted-foreground">{description}</div>}
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2">{children}</div>
    </div>
  )
}

/** Red text + outline: destructive actions in a danger section (the confirm dialogs stay solid red). */
export const dangerButtonClass =
  'border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive dark:border-destructive/50 dark:hover:bg-destructive/15'

/** The shared input look of the settings forms (unchanged from the old pages). */
export const settingsInputClass =
  'h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-foreground/30 disabled:opacity-60'
